import { PARTY_TRUCES_MAX } from "./constants.ts";

/**
 * The truce table's rules (ADR-101 decision 10): a truce after a parting, one per partner, ending at a time in
 * milliseconds since the epoch, and at most `PARTY_TRUCES_MAX` live at once, so none is ever dropped early. Plain data
 * and pure functions: the gateway keeps the list in the store and the map server turns each end into a tick.
 */

/**
 * One truce a character holds: the other character and the end, in milliseconds since the epoch.
 */
export interface Truce {
  readonly other: number;
  readonly until: number;
}

/**
 * The truces still running at a time: those ending after it.
 *
 * @param truces The character's truces.
 * @param now The time in milliseconds since the epoch.
 * @returns A new list of the live ones, in their order.
 */
export function liveTruces(truces: readonly Truce[], now: number): Truce[] {
  return truces.filter((truce) => truce.until > now);
}

/**
 * Whether a character is in truce with another at a time.
 *
 * @param truces The character's truces.
 * @param other The other character.
 * @param now The time in milliseconds since the epoch.
 * @returns True while a truce with that partner ends after now.
 */
export function inTruce(truces: readonly Truce[], other: number, now: number): boolean {
  return truces.some((truce) => truce.other === other && truce.until > now);
}

/**
 * A character's truces with one more written: the expired dropped, one truce a partner with the later end, ordered by
 * end so the oldest comes first.
 *
 * @param truces The character's truces.
 * @param other The partner of the new truce.
 * @param until The new truce's end.
 * @param now The time in milliseconds since the epoch.
 * @returns A new list.
 */
export function withTruce(truces: readonly Truce[], other: number, until: number, now: number): Truce[] {
  const kept = liveTruces(truces, now);
  const held = kept.find((truce) => truce.other === other);
  const rest = kept.filter((truce) => truce.other !== other);
  rest.push({ other, until: Math.max(until, held?.until ?? 0) });
  return rest.sort((a, b) => a.until - b.until);
}

/**
 * How long the live truces of one character must run before it can take `gain` more without passing the cap.
 *
 * @param truces The character's truces.
 * @param gain The truces the accept could add.
 * @param now The time in milliseconds since the epoch.
 * @returns 0 when there is room, else the milliseconds until enough of its oldest end.
 */
function roomWait(truces: readonly Truce[], gain: number, now: number): number {
  const ends = liveTruces(truces, now)
    .map((truce) => truce.until)
    .sort((a, b) => a - b);
  const over = ends.length + gain - PARTY_TRUCES_MAX;
  return over > 0 ? (ends[over - 1] as number) - now : 0;
}

/**
 * The wait before an accept may go ahead, so that no truce is dropped early (decision 10): the accepter could part
 * from each of the `seated` members it joins, and each of those members could part from the accepter, so the accepter
 * must have room for `seated` more truces and each member for one. The accept is refused `recent` with the longest wait.
 *
 * @param accepter The accepting character's truces.
 * @param seated The members seated when it accepts.
 * @param members Those members' truces.
 * @param now The time in milliseconds since the epoch.
 * @returns 0 when every character has room, else the milliseconds until all do.
 */
export function truceWait(accepter: readonly Truce[], seated: number, members: readonly (readonly Truce[])[], now: number): number {
  let wait = roomWait(accepter, seated, now);
  for (const truces of members) {
    wait = Math.max(wait, roomWait(truces, 1, now));
  }
  return wait;
}
